import { create } from 'zustand';

import {
  MENU_CATEGORY_KEYS,
  type MenuCategoryKey,
  type PermissionMap,
  type PermissionValue,
  type Role,
} from '../types/role.types';

type Mode = 'list' | 'view' | 'edit' | 'create';

interface RoleDraft {
  name: string;
  permissionMap: PermissionMap;
}

interface RoleDetailState {
  mode: Mode;
  currentRoleId: number | null;
  // SHCT-633 — the detail screen prints 최초 생성 / 최근 업데이트 (PRD 16 §4.1), so the
  // opened Role's timestamps ride alongside the draft. Deliberately NOT part of
  // `RoleDraft`: the draft is diffed against `snapshot` by `isDirty`, and a
  // server-owned read-only field there would be dead weight in that comparison.
  // `null` in create mode — the row does not exist yet.
  currentRoleCreatedAt: string | null;
  currentRoleUpdatedAt: string | null;
  draft: RoleDraft | null;
  snapshot: RoleDraft | null;
  nameError: string | null;
  openList: () => void;
  openView: (role: Role) => void;
  openEdit: (role: Role) => void;
  openCreate: () => void;
  updateDraftName: (name: string) => void;
  updateDraftPermission: (cat: MenuCategoryKey, val: PermissionValue) => void;
  setNameError: (err: string | null) => void;
  isDirty: () => boolean;
}

const emptyPermissionMap = (): PermissionMap =>
  MENU_CATEGORY_KEYS.reduce<PermissionMap>((acc, key) => {
    acc[key] = 'NO_PERMISSION';
    return acc;
  }, {} as PermissionMap);

const toDraft = (role: Role): RoleDraft => ({
  name: role.name,
  permissionMap: { ...role.permissionMap },
});

export const useRoleDetailStore = create<RoleDetailState>((set, get) => ({
  mode: 'list',
  currentRoleId: null,
  currentRoleCreatedAt: null,
  currentRoleUpdatedAt: null,
  draft: null,
  snapshot: null,
  nameError: null,

  openList: () =>
    set({
      mode: 'list',
      currentRoleId: null,
      currentRoleCreatedAt: null,
      currentRoleUpdatedAt: null,
      draft: null,
      snapshot: null,
      nameError: null,
    }),

  openView: (role) =>
    set({
      mode: 'view',
      currentRoleId: role.id,
      currentRoleCreatedAt: role.createdAt,
      currentRoleUpdatedAt: role.updatedAt,
      draft: toDraft(role),
      snapshot: toDraft(role),
      nameError: null,
    }),

  openEdit: (role) =>
    set({
      mode: 'edit',
      currentRoleId: role.id,
      currentRoleCreatedAt: role.createdAt,
      currentRoleUpdatedAt: role.updatedAt,
      draft: toDraft(role),
      snapshot: toDraft(role),
      nameError: null,
    }),

  openCreate: () => {
    const empty: RoleDraft = { name: '', permissionMap: emptyPermissionMap() };
    set({
      mode: 'create',
      currentRoleId: null,
      currentRoleCreatedAt: null,
      currentRoleUpdatedAt: null,
      draft: empty,
      snapshot: { name: empty.name, permissionMap: { ...empty.permissionMap } },
      nameError: null,
    });
  },

  updateDraftName: (name) =>
    set((s) => (s.draft ? { draft: { ...s.draft, name }, nameError: null } : {})),

  updateDraftPermission: (cat, val) =>
    set((s) =>
      s.draft
        ? {
            draft: {
              ...s.draft,
              permissionMap: { ...s.draft.permissionMap, [cat]: val },
            },
          }
        : {},
    ),

  setNameError: (err) => set({ nameError: err }),

  isDirty: () => {
    const { draft, snapshot } = get();
    if (!draft || !snapshot) return false;
    if (draft.name !== snapshot.name) return true;
    return MENU_CATEGORY_KEYS.some(
      (cat) => draft.permissionMap[cat] !== snapshot.permissionMap[cat],
    );
  },
}));
